// This is the main application component that sets up routing and global state.
import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';

// IMPORT YOUR MAIN PAGE COMPONENTS HERE
import HomePage from './components/HomePage';
import AboutPage from './components/AboutPage';
import CVPage from './components/CVPage';
import EducationPage from './components/EducationPage';
import ProjectsPage from './components/ProjectsPage';
import ResearchPage from './components/ResearchPage';
import ExperiencePage from './components/ExperiencePage';
import CertificationsPage from './components/CertificationsPage';


// IMPORT CUSTOM PUBLICATION PAGES HERE
import RPETPage from './components/publications/RPETPage';
import E2EPdMPage from './components/publications/E2EPdMPage';
import AIAnomalyPage from './components/publications/AIAnomalyPage';
import SupersonicWingPage from './components/publications/SupersonicWingPage';

// IMPORT CUSTOM PROJECT PAGES HERE
import VoiceDronePage from './components/projects/VoiceDronePage';
import SkySentinelPage from './components/projects/SkySentinelPage';
import MedicalDeliveryUAVPage from './components/projects/MedicalDeliveryUAVPage';
import WeatherSatellitePage from './components/projects/WeatherSatellitePage';
import E2EPdMProjectPage from './components/projects/E2EPdMProjectPage';
import RPETProjectPage from './components/projects/RPETProjectPage';
import SkyBeeProjectPage from './components/projects/SkyBeeProjectPage';


// IMPORT UTILITIES
import { defaultData, deepMerge, loadExternalData } from './utils/data';

// MAIN APP COMPONENT
export default function App() {
  const [data, setData] = useState(defaultData);

// Load external data on mount and merge with default data  
  useEffect(() => {
    (async () => {
      try {
        const external = await loadExternalData();
        setData((prev) => deepMerge(prev, external));
      } catch (err) {
        console.error('Failed to load external data', err);
      }
    })();
  }, []);

  // Retire the old theme preference and always use the portfolio's light palette.
  useEffect(() => {
    document.documentElement.classList.remove('dark');
    try { localStorage.removeItem('portfolio-theme'); } catch {}
  }, []);

  return (
    <Router>
      <MainLayout data={data} />
    </Router>
  );
}

// MAIN LAYOUT COMPONENT THAT WRAPS ALL PAGES WITH NAVIGATION AND FOOTER
function MainLayout({ data }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const navRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    setMenuOpen(false);
  }, [location.pathname]);

  // DEFINE THE MAIN NAVIGATION PAGES AND THEIR ORDER
  const navPages = ['home', 'research', 'projects', 'experience', 'education', 'about', 'certifications', 'cv']; 
  
  // Dismiss the dropdown by pressing Escape or clicking outside the navigation.
  useEffect(() => {
    if (!menuOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        navRef.current?.querySelector('.nav-toggle')?.focus();
      }
    };
    const handlePointerDown = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    const handleResize = () => {
      if (window.innerWidth > 1080) setMenuOpen(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('resize', handleResize);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('resize', handleResize);
    };
  }, [menuOpen]);


  // PREPARE FOOTER ICONS BASED ON AVAILABLE CONTACT INFO IN THE PROFILE
  const footerIcons = [];
  const profileLinks = data.profile?.links || {};
  if (data.profile?.github || profileLinks.github) footerIcons.push({ img: '/assets/icons/github.png', href: data.profile?.github || profileLinks.github, label: 'GitHub' });
  if (data.profile?.linkedin || profileLinks.linkedin) footerIcons.push({ img: '/assets/icons/linkedin.png', href: data.profile?.linkedin || profileLinks.linkedin, label: 'LinkedIn' });
  if (data.profile?.email) footerIcons.push({ img: '/assets/icons/gmail.png', href: `mailto:${data.profile.email}`, label: 'Email' });

  const userEmail = data.profile?.email || 'jolok21014026@aaub.edu.bd';
  const userLinkedIn = data.profile?.linkedin || profileLinks.linkedin || 'https://www.linkedin.com/in/jolok-banarjee-16825b1b7/';

  // RENDER THE MAIN LAYOUT WITH NAVIGATION, ROUTES, AND FOOTER
  return (
    <div className="container">
      <a className="skip-link" href="#main-content">Skip to content</a>
      {/* Clean desktop navigation and accessible mobile dropdown. */}
      <nav
        ref={navRef}
        aria-label="Main navigation"
        className={`top-nav${menuOpen ? ' menu-open' : ''}`}
      >
        <Link
          className="nav-brand"
          to="/"
          aria-label="Jolok Banarjee — home"
          onClick={() => setMenuOpen(false)}
        >
          <span className="nav-name">{data.profile?.name || 'Jolok Banarjee'}</span>
        </Link>

        <button
          type="button"
          className="nav-toggle"
          aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          aria-expanded={menuOpen}
          aria-controls="primary-links"
          onClick={() => setMenuOpen((open) => !open)}
        >
          {menuOpen ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
              <path d="M5 5l14 14M19 5L5 19" />
            </svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          )}
        </button>

        <div id="primary-links" className="nav-links">
          {navPages.map((pg) => {
            const path = pg === 'home' ? '/' : `/${pg}`;
            const isActive = location.pathname === path || (pg !== 'home' && location.pathname.startsWith(path + '/'));
            return (
              <Link
                key={pg}
                to={path}
                aria-current={isActive ? 'page' : undefined}
                className={`nav-link${isActive ? ' active' : ''}`}
                onClick={() => setMenuOpen(false)}
              >
                {pg === 'cv' ? 'CV' : pg.charAt(0).toUpperCase() + pg.slice(1)}
              </Link>
            );
          })}
          <a
            className="nav-contact nav-contact-mobile"
            href={`mailto:${userEmail}`}
            onClick={() => setMenuOpen(false)}
          >
            Contact <span aria-hidden="true">↗</span>
          </a>
        </div>

        <a
          className="nav-contact nav-contact-desktop"
          href={`mailto:${userEmail}`}
        >
          Contact <span aria-hidden="true">↗</span>
        </a>
      </nav>

      {/* MAIN ROUTING CONTENT */}
      <main onErrorCapture={(event) => {
        const image = event.target;
        if (image.tagName === 'IMG' && !image.dataset.fallback) {
          image.dataset.fallback = 'true';
          image.src = '/assets/image-unavailable.svg';
        }
      }} id="main-content" tabIndex={-1} className="main-content">
        <Routes>
          <Route path="/" element={<HomePage data={data} />} />
          <Route path="/about" element={<AboutPage data={data} />} />
          <Route path="/certifications" element={<CertificationsPage data={data} />} />
          <Route path="/cv" element={<CVPage data={data} />} />
          <Route path="/education" element={<EducationPage data={data} />} />
          <Route path="/experience" element={<ExperiencePage data={data} />} />

          <Route path="/projects" element={<ProjectsPage data={data} />} />
          <Route path="/projects/voice-controlled-drone" element={<VoiceDronePage />} />
          <Route path="/projects/sky-sentinel-s26" element={<SkySentinelPage />} />
          <Route path="/projects/medical-delivery-uav" element={<MedicalDeliveryUAVPage />} />
          <Route path="/projects/weather-satellite-ground-station" element={<WeatherSatellitePage />} />
          <Route path="/projects/e2e-pdm-framework" element={<E2EPdMProjectPage />} />
          <Route path="/projects/rpet-additive-manufacturing" element={<RPETProjectPage />} />
          <Route path="/projects/skybee-homeland-defense-interceptor" element={<SkyBeeProjectPage />} />

          <Route path="/research" element={<ResearchPage data={data} />} />

          {/* CONNECT THE SLUGS TO THE COMPONENTS */}
          <Route path="/research/e2e-pdm-framework" element={<E2EPdMPage />} />
          <Route path="/research/ai-anomaly-detection" element={<AIAnomalyPage />} />
          <Route path="/research/rpet-additive-manufacturing" element={<RPETPage />} />
          <Route path="/research/supersonic-wing-analysis" element={<SupersonicWingPage />} />

          <Route path="*" element={<section className="not-found"><h1>Page not found</h1><p>This page may have moved.</p><Link to="/research">Browse research</Link></section>} />
        </Routes>
      </main>

      {/* FOOTER */}
      <footer className="footer">
        {/* Custom Portfolio Developer Attribution & Inquiry Banner */}
        <div className="footer-dev-banner">
          <div className="footer-dev-text">
            <span className="footer-dev-badge">💻 CUSTOM PORTFOLIO ARCHITECTURE</span>
            <h4 className="footer-dev-heading">
              This Website Was Entirely Developed by Myself
            </h4>
            <p className="footer-dev-desc">
              Designed, architected, and coded from scratch by <strong>Jolok Banarjee</strong> using React.js and modern CSS. If anyone wants to build a similar custom academic, research, or professional portfolio website, feel free to contact me!
            </p>
          </div>
          <div className="footer-dev-actions">
            <a
              href={`mailto:${userEmail}?subject=Inquiry%3A%20Building%20a%20Similar%20Portfolio%20Website`}
              className="footer-dev-btn-primary"
            >
              ✉️ Contact Me to Build Yours
            </a>
            <a
              href={userLinkedIn}
              target="_blank"
              rel="noopener noreferrer"
              className="footer-dev-btn-secondary"
            >
              Message on LinkedIn ↗
            </a>
          </div>
        </div>

        <div className="footer-connect">
          <h3 className="footer-title">Connect With Me</h3>
          <div className="footer-icons">
            {footerIcons.map((item, idx) => (
              <a key={idx} href={item.href} target="_blank" rel="noopener noreferrer" className="connect-icon" title={item.label}>
                <img src={item.img} alt={item.label} style={{ width: '28px', height: '28px', borderRadius: '6px' }} />
              </a>
            ))}
          </div>
        </div>
        <div className="footer-credits">
          © {new Date().getFullYear()} {data.profile?.name || 'Jolok Banarjee'}. Entirely designed & developed by {data.profile?.name || 'Jolok Banarjee'}. All rights reserved.
        </div>
      </footer>
    </div>
  );
}